import {
  BRAINSTORM_DAYS,
  puzzleOfDay,
  puzzleStreak,
  todayIso,
  useAnswerPuzzle,
  usePuzzleAnswers,
} from '@shkola/api';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GRAD, INK, INK_FAINT, INK_SOFT, PAPER, grad } from '../design/forms';
import { CheckIcon } from '../design/icons';
import { plain } from './home/model';

/*
 * «Загадка дня» in «Форми 2» (28.10): a white block, the title light, the streak as a soft butter label;
 * the answers as pills to tap — the right one turns mint with a tick, a wrong pick fades and is struck.
 */

/** «Загадка дня»: one tap to answer, the explanation, and the streak of days in a row. */
export function PuzzleCard({
  schoolId,
  studentId,
  className = '',
}: {
  schoolId: string;
  studentId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const today = todayIso();
  const puzzle = puzzleOfDay(today);
  const answers = usePuzzleAnswers(studentId);
  const answer = useAnswerPuzzle();
  const [picked, setPicked] = useState<number>();
  const todays = (answers.data ?? []).find((a) => a.day === today);
  const answered = picked !== undefined || todays !== undefined;
  const right = picked !== undefined ? picked === puzzle.correct : todays?.correct;
  const days = [
    ...(answers.data ?? []).map((a) => a.day),
    ...(picked !== undefined ? [today] : []),
  ];
  const streak = puzzleStreak(days, today);
  return (
    <section className={`flex flex-col gap-4 rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2
          className="font-display text-[30px] leading-[36px] font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {t('puzzle.title')}
        </h2>
        {streak > 0 && (
          <span
            className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[13px] font-medium"
            style={{ backgroundImage: grad('butter'), color: GRAD.butter.ink }}
          >
            {plain(t('puzzle.streak', { count: streak }))}
          </span>
        )}
      </div>
      <p className="text-[18px] leading-[26px]" style={{ color: INK }}>
        {puzzle.text}
      </p>
      <div className="flex flex-wrap gap-2">
        {puzzle.options.map((option, i) => {
          const isRight = answered && i === puzzle.correct;
          const isWrong = picked === i && i !== puzzle.correct;
          return (
            <button
              key={option}
              type="button"
              disabled={answered}
              onClick={() => {
                setPicked(i);
                answer.mutate({ schoolId, studentId, correct: i === puzzle.correct });
              }}
              className={`inline-flex h-11 items-center gap-1.5 rounded-full px-5 text-[15px] font-medium transition-colors ${isWrong ? 'line-through' : ''} ${answered ? '' : 'hover:bg-[#EFECE5]'}`}
              style={
                isRight
                  ? { backgroundImage: grad('mint'), color: GRAD.mint.ink }
                  : { backgroundColor: PAPER, color: isWrong ? INK_FAINT : INK }
              }
            >
              {isRight && <CheckIcon size={14} weight="bold" aria-hidden />}
              {option}
            </button>
          );
        })}
      </div>
      {answered && (
        <div className="motion-rise flex flex-col gap-1 text-[15px]">
          <p className="font-medium" style={{ color: INK }}>
            {plain(right ? t('puzzle.right') : t('puzzle.wrong'))}
          </p>
          <p style={{ color: INK_SOFT }}>{puzzle.explain}</p>
          <p className="text-[13px]" style={{ color: INK_FAINT }}>
            {streak >= BRAINSTORM_DAYS
              ? plain(t('puzzle.badge'))
              : t('puzzle.toBadge', { count: BRAINSTORM_DAYS - streak })}{' '}
            · {t('puzzle.tomorrow')}
          </p>
        </div>
      )}
    </section>
  );
}
